import { createContext, type ReactNode, useContext, useEffect, useState } from 'react';
import { isPostHogConfigured, posthog } from '@/app/analytics/posthog.ts';
import toast from '@/app/toast.ts';
import type { UserModel } from '../types/user-type.ts';
import type {
  LogInWithOtpRequest,
  LogInWithPasswordRequest,
  RequestPasswordResetRequest,
  ResetPasswordRequest,
  SendLoginOtpRequest,
  SetPasswordRequest,
  StartLoginRequest,
  StartLoginResponse,
} from './auth.types.ts';

interface AuthContextType {
  user: UserModel | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isImpersonating: boolean;
  startLogin: (request: StartLoginRequest) => Promise<StartLoginResponse>;
  sendLoginOtp: (request: SendLoginOtpRequest) => Promise<boolean>;
  logInWithOtp: (request: LogInWithOtpRequest) => Promise<UserModel | null>;
  logInWithPassword: (request: LogInWithPasswordRequest) => Promise<UserModel | null>;
  setPassword: (request: SetPasswordRequest) => Promise<void>;
  requestPasswordReset: (request: RequestPasswordResetRequest) => Promise<void>;
  resetPassword: (request: ResetPasswordRequest) => Promise<UserModel | null>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  impersonateUser: (targetUserKey: string) => Promise<void>;
  stopImpersonating: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserModel | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isImpersonating, setIsImpersonating] = useState(false);

  const checkAuthStatus = async (): Promise<UserModel | null> => {
    try {
      const response = await fetch(`${import.meta.env.VITE_BASE_API_URL}/api/Auth/Status`, {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        if (data.userIsAuthenticated) {
          setIsImpersonating(data.userIsImpersonating || false);
          return await fetchUser();
        }
        setUser(null);
        setIsImpersonating(false);
        return null;
      }
      setUser(null);
      setIsImpersonating(false);
      return null;
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
      setIsImpersonating(false);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUser = async (): Promise<UserModel | null> => {
    try {
      const response = await fetch(`${import.meta.env.VITE_BASE_API_URL}/api/User/LoggedIn`, {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      });
      if (response.ok) {
        const userData = (await response.json()) as UserModel;
        setUser(userData);
        return userData;
      }
      setUser(null);
      setIsImpersonating(false);
      return null;
    } catch (error) {
      console.error('Failed to fetch user:', error);
      setUser(null);
      setIsImpersonating(false);
      return null;
    }
  };

  const startLogin = async (request: StartLoginRequest) => {
    const response = await fetch(`${import.meta.env.VITE_BASE_API_URL}/api/Auth/StartLogin`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Login failed');
    }

    return await response.json();
  };

  const sendLoginOtp = async (request: SendLoginOtpRequest) => {
    const response = await fetch(`${import.meta.env.VITE_BASE_API_URL}/api/Auth/SendLoginOtp`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Login failed');
    }

    await response.json();
    return true;
  };

  const logInWithOtp = async (request: LogInWithOtpRequest) => {
    const response = await fetch(`${import.meta.env.VITE_BASE_API_URL}/api/Auth/LogInWithOtp`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Invalid code');
    }

    setAccessToken(await getAccessTokenFromResponse(response));
    return checkAuthStatus();
  };

  const logInWithPassword = async (request: LogInWithPasswordRequest) => {
    const response = await fetch(
      `${import.meta.env.VITE_BASE_API_URL}/api/Auth/LogInWithPassword`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      },
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Invalid password');
    }

    setAccessToken(await getAccessTokenFromResponse(response));
    return checkAuthStatus();
  };

  const setPassword = async (request: SetPasswordRequest) => {
    const response = await fetch(`${import.meta.env.VITE_BASE_API_URL}/api/Auth/SetPassword`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getAccessToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to set password');
    }
  };

  const requestPasswordReset = async (request: RequestPasswordResetRequest) => {
    const response = await fetch(
      `${import.meta.env.VITE_BASE_API_URL}/api/Auth/RequestPasswordReset`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      },
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to request password reset');
    }
  };

  const resetPassword = async (request: ResetPasswordRequest) => {
    const response = await fetch(`${import.meta.env.VITE_BASE_API_URL}/api/Auth/ResetPassword`, {
      method: 'POST',

      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to reset password');
    }

    setAccessToken(await getAccessTokenFromResponse(response));
    return checkAuthStatus();
  };

  const logout = async () => {
    setUser(null);
    setIsImpersonating(false);
    clearAccessToken();
    if (isPostHogConfigured) {
      posthog.capture('user_logged_out');
      posthog.reset();
    }
  };

  const refreshUser = async () => {
    await checkAuthStatus();
  };

  const impersonateUser = async (targetUserKey: string) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_BASE_API_URL}/api/Impersonation/Start`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ targetUserKey }),
      });

      if (response.ok) {
        setAccessToken(await getAccessTokenFromResponse(response));
        if (isPostHogConfigured) {
          posthog.reset();
        }
        window.location.href = '/';
      } else {
        toast.error('Failed to start impersonation');
      }
    } catch {
      toast.error('Failed to start impersonation');
    }
  };

  const stopImpersonating = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_BASE_API_URL}/api/Impersonation/Stop`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      });

      if (response.ok) {
        setAccessToken(await getAccessTokenFromResponse(response));
        if (isPostHogConfigured) {
          posthog.reset();
        }
        window.location.href = '/';
      } else {
        toast.error('Failed to stop impersonation');
      }
    } catch {
      toast.error('Failed to stop impersonation');
    }
  };

  useEffect(() => {
    checkAuthStatus();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        isImpersonating,
        startLogin,
        sendLoginOtp,
        logInWithOtp,
        logInWithPassword,
        setPassword,
        requestPasswordReset,
        resetPassword,
        logout,
        refreshUser,
        impersonateUser,
        stopImpersonating,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

const accessTokenStorageKey = 'mmd-api:accessToken';

export function getAccessToken() {
  return (
    sessionStorage.getItem(accessTokenStorageKey) || localStorage.getItem(accessTokenStorageKey)
  );
}

async function getAccessTokenFromResponse(response: Response) {
  const data = await response.json();
  const accessToken = data.accessToken as string;

  if (!accessToken) {
    throw new Error('Access token not found in response');
  }

  return accessToken;
}

function setAccessToken(accessToken: string) {
  localStorage.setItem(accessTokenStorageKey, accessToken);
}

function clearAccessToken() {
  localStorage.removeItem(accessTokenStorageKey);
  sessionStorage.removeItem(accessTokenStorageKey);
}
